import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Clock, Bell, Repeat, ListChecks } from "lucide-react";
import EventDialog from "@/components/EventDialog";

function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function taskAppliesToDate(task, date) {
  if (task.recurring === "none") return task.date === date;
  if (task.recurring === "daily") return task.date <= date;
  const [Y, M, D] = date.split("-").map(Number);
  const dow = new Date(Y, M - 1, D).getDay(); // 0 sun .. 6 sat
  if (task.recurring === "weekly") {
    const [tY, tM, tD] = task.date.split("-").map(Number);
    const taskDow = new Date(tY, tM - 1, tD).getDay();
    return task.date <= date && dow === taskDow;
  }
  if (task.recurring === "weekdays") {
    return task.date <= date && dow >= 1 && dow <= 5;
  }
  return false;
}

function taskCompletedOn(task, date) {
  if (task.recurring === "none") return !!task.completed;
  return Array.isArray(task.completed_dates) && task.completed_dates.includes(date);
}

export default function DayPanel({ selectedDate, events, onEventsChanged }) {
  const dateKey = fmtDate(selectedDate);
  const [tasks, setTasks] = useState([]);
  const [newTitle, setNewTitle] = useState("");
  const [newRecurring, setNewRecurring] = useState("none");
  const [eventDialogOpen, setEventDialogOpen] = useState(false);

  const loadTasks = async () => {
    const { data } = await api.get("/tasks", { params: { date: dateKey } });
    setTasks(data);
  };

  useEffect(() => {
    loadTasks();
     
  }, [dateKey]);

  const dayEvents = useMemo(
    () => events.filter((e) => e.date === dateKey).sort((a, b) => (a.time || "").localeCompare(b.time || "")),
    [events, dateKey]
  );

  const visibleTasks = useMemo(
    () => tasks.filter((t) => taskAppliesToDate(t, dateKey)),
    [tasks, dateKey]
  );

  const addTask = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    await api.post("/tasks", {
      title: newTitle.trim(),
      date: dateKey,
      recurring: newRecurring,
    });
    setNewTitle("");
    setNewRecurring("none");
    loadTasks();
  };

  const toggleTask = async (task) => {
    const completed = !taskCompletedOn(task, dateKey);
    const params = task.recurring !== "none" ? { date: dateKey } : {};
    await api.patch(`/tasks/${task.id}`, { completed }, { params });
    loadTasks();
  };

  const deleteTask = async (task) => {
    await api.delete(`/tasks/${task.id}`);
    loadTasks();
  };

  const deleteEvent = async (ev) => {
    await api.delete(`/events/${ev.id}`);
    onEventsChanged && onEventsChanged();
  };

  const niceDate = selectedDate.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-sm space-y-6" data-testid="day-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Selected day
          </div>
          <h3 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight mt-0.5">
            {niceDate}
          </h3>
        </div>
        <Button
          onClick={() => setEventDialogOpen(true)}
          className="rounded-xl"
          data-testid="add-event-button"
        >
          <Plus strokeWidth={1.5} className="size-4 mr-1.5" />
          New event
        </Button>
      </div>

      {/* Events */}
      <section className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Clock strokeWidth={1.5} className="size-4 text-primary" />
          Events
        </div>
        {dayEvents.length === 0 && (
          <p className="text-sm text-muted-foreground py-3">No events scheduled.</p>
        )}
        <ul className="space-y-2">
          {dayEvents.map((ev) => (
            <li
              key={ev.id}
              data-testid={`event-item-${ev.id}`}
              className="group flex items-start gap-3 rounded-xl border border-border bg-background p-3 hover:border-primary/40 transition-colors"
            >
              <div className="size-2 rounded-full bg-primary mt-2 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{ev.title}</div>
                {ev.description && (
                  <div className="text-xs text-muted-foreground mt-0.5">{ev.description}</div>
                )}
                <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                  {ev.time && <span className="font-medium">{ev.time}</span>}
                  {ev.reminder_minutes_before != null && (
                    <span className="inline-flex items-center gap-1">
                      <Bell strokeWidth={1.5} className="size-3" />
                      {ev.reminder_minutes_before === 0
                        ? "At time"
                        : ev.reminder_minutes_before >= 1440
                        ? `${ev.reminder_minutes_before / 1440}d before`
                        : ev.reminder_minutes_before >= 60
                        ? `${ev.reminder_minutes_before / 60}h before`
                        : `${ev.reminder_minutes_before}m before`}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => deleteEvent(ev)}
                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all"
                data-testid={`event-delete-${ev.id}`}
                aria-label="Delete event"
              >
                <Trash2 strokeWidth={1.5} className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      </section>

      {/* Tasks */}
      <section className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <ListChecks strokeWidth={1.5} className="size-4 text-primary" />
          Daily checklist
        </div>
        <form onSubmit={addTask} className="flex flex-col sm:flex-row gap-2">
          <Input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Add a task..."
            className="h-10"
            data-testid="task-input"
          />
          <div className="flex gap-2">
            <Select value={newRecurring} onValueChange={setNewRecurring}>
              <SelectTrigger className="w-[140px] h-10" data-testid="task-recurring-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">One-time</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekdays">Weekdays</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" className="rounded-xl shrink-0" data-testid="task-add-button">
              <Plus strokeWidth={1.5} className="size-4" />
            </Button>
          </div>
        </form>
        {visibleTasks.length === 0 && (
          <p className="text-sm text-muted-foreground py-3">No tasks for this day yet.</p>
        )}
        <ul className="space-y-1.5">
          {visibleTasks.map((t) => {
            const done = taskCompletedOn(t, dateKey);
            return (
              <li
                key={t.id}
                data-testid={`task-item-${t.id}`}
                className="group flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-secondary/60 transition-colors"
              >
                <Checkbox
                  checked={done}
                  onCheckedChange={() => toggleTask(t)}
                  data-testid={`task-checkbox-${t.id}`}
                />
                <span
                  className={`flex-1 text-sm transition-all ${
                    done ? "line-through text-muted-foreground" : ""
                  }`}
                >
                  {t.title}
                </span>
                {t.recurring !== "none" && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground border border-border rounded-md px-1.5 py-0.5">
                    <Repeat strokeWidth={1.5} className="size-3" />
                    {t.recurring}
                  </span>
                )}
                <button
                  onClick={() => deleteTask(t)}
                  className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all"
                  data-testid={`task-delete-${t.id}`}
                  aria-label="Delete task"
                >
                  <Trash2 strokeWidth={1.5} className="size-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      <EventDialog
        open={eventDialogOpen}
        onOpenChange={setEventDialogOpen}
        date={dateKey}
        onSaved={onEventsChanged}
      />
    </div>
  );
}
